import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  Download,
  ArrowLeft,
  FileJson,
  FileText,
  Users,
  Briefcase,
  FileSpreadsheet,
  BarChart3,
  CheckCircle,
  Table
} from "lucide-react";

// Helper to convert JSON array to CSV
const jsonToCSV = (data, fields = null) => {
  if (!data || data.length === 0) return "";
  
  const headers = fields || Object.keys(data[0]);
  const csvRows = [headers.join(",")];
  
  for (const row of data) {
    const values = headers.map(header => {
      let val = row[header];
      if (val === null || val === undefined) val = "";
      if (typeof val === "object") val = JSON.stringify(val);
      // Escape quotes and wrap in quotes if contains comma
      val = String(val).replace(/"/g, '""');
      if (val.includes(",") || val.includes("\n") || val.includes('"')) {
        val = `"${val}"`;
      }
      return val;
    });
    csvRows.push(values.join(","));
  }
  
  return csvRows.join("\n");
};

const AdminExport = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exporting, setExporting] = useState("");
  const [selectedFormat, setSelectedFormat] = useState({});

  if (user?.role !== "admin") {
    navigate("/");
    return null;
  }

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportAnalytics = async (format = "json") => {
    setExporting("analytics");
    try {
      const res = await fetch(`${API}/admin/export/analytics?format=json`, {
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        const dateStr = new Date().toISOString().split("T")[0];
        
        if (format === "csv") {
          // Flatten analytics data for CSV
          const flatData = [{
            total_users: data.total_users,
            total_jobs: data.total_jobs,
            total_applications: data.total_applications,
            total_interviews: data.total_interviews,
            total_messages: data.total_messages,
            active_jobs: data.active_jobs,
            completed_interviews: data.completed_interviews,
            ...data.subscription_breakdown
          }];
          const csv = jsonToCSV(flatData);
          downloadFile(csv, `rekrutor_analytics_${dateStr}.csv`, "text/csv");
        } else {
          downloadFile(JSON.stringify(data, null, 2), `rekrutor_analytics_${dateStr}.json`, "application/json");
        }
        toast.success("Analytics exported successfully!");
      } else {
        toast.error("Failed to export analytics");
      }
    } catch (error) {
      toast.error("Failed to export analytics");
    } finally {
      setExporting("");
    }
  };

  const exportJobs = async (format = "json") => {
    setExporting("jobs");
    try {
      const res = await fetch(`${API}/admin/export/jobs`, {
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        const dateStr = new Date().toISOString().split("T")[0];
        
        if (format === "csv") {
          const csv = jsonToCSV(data.jobs, ["job_id", "title", "company", "location", "salary_min", "salary_max", "status", "applicant_count", "recruiter_name", "recruiter_email", "created_at"]);
          downloadFile(csv, `rekrutor_jobs_${dateStr}.csv`, "text/csv");
        } else {
          downloadFile(JSON.stringify(data.jobs, null, 2), `rekrutor_jobs_${dateStr}.json`, "application/json");
        }
        toast.success(`Exported ${data.total} jobs!`);
      } else {
        toast.error("Failed to export jobs");
      }
    } catch (error) {
      toast.error("Failed to export jobs");
    } finally {
      setExporting("");
    }
  };

  const exportApplications = async (format = "json") => {
    setExporting("applications");
    try {
      const res = await fetch(`${API}/admin/export/applications`, {
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        const dateStr = new Date().toISOString().split("T")[0];
        
        if (format === "csv") {
          const csv = jsonToCSV(data.applications, ["application_id", "job_title", "company", "candidate_name", "candidate_email", "status", "match_score", "applied_at"]);
          downloadFile(csv, `rekrutor_applications_${dateStr}.csv`, "text/csv");
        } else {
          downloadFile(JSON.stringify(data.applications, null, 2), `rekrutor_applications_${dateStr}.json`, "application/json");
        }
        toast.success(`Exported ${data.total} applications!`);
      } else {
        toast.error("Failed to export applications");
      }
    } catch (error) {
      toast.error("Failed to export applications");
    } finally {
      setExporting("");
    }
  };

  const exportUsers = async (format = "json") => {
    setExporting("users");
    try {
      const res = await fetch(`${API}/admin/users/bulk/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          filters: {},
          fields: ["user_id", "email", "name", "role", "subscription_tier", "created_at", "status"]
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const dateStr = new Date().toISOString().split("T")[0];
        
        if (format === "csv") {
          const csv = jsonToCSV(data.users, ["user_id", "email", "name", "role", "subscription_tier", "created_at", "status"]);
          downloadFile(csv, `rekrutor_users_${dateStr}.csv`, "text/csv");
        } else {
          downloadFile(JSON.stringify(data.users, null, 2), `rekrutor_users_${dateStr}.json`, "application/json");
        }
        toast.success(`Exported ${data.total} users!`);
      } else {
        toast.error("Failed to export users");
      }
    } catch (error) {
      toast.error("Failed to export users");
    } finally {
      setExporting("");
    }
  };

  const exportOptions = [
    {
      id: "analytics",
      title: "Platform Analytics",
      description: "Export comprehensive platform statistics including users, jobs, applications, interviews, and subscription data",
      icon: BarChart3,
      action: exportAnalytics,
      color: "text-blue-400",
      bgColor: "bg-blue-500/20"
    },
    {
      id: "users",
      title: "All Users",
      description: "Export all user accounts with profile information, roles, and subscription status",
      icon: Users,
      action: exportUsers,
      color: "text-purple-400",
      bgColor: "bg-purple-500/20"
    },
    {
      id: "jobs",
      title: "All Jobs",
      description: "Export all job postings with recruiter info and applicant counts",
      icon: Briefcase,
      action: exportJobs,
      color: "text-orange-400",
      bgColor: "bg-orange-500/20"
    },
    {
      id: "applications",
      title: "All Applications",
      description: "Export all job applications with candidate and job details",
      icon: FileText,
      action: exportApplications,
      color: "text-cyan-400",
      bgColor: "bg-cyan-500/20"
    }
  ];

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-export-page">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold mb-2">
              DATA <span className="text-crimson">EXPORT</span>
            </h1>
            <p className="text-gray-400">Download comprehensive data exports for analysis and reporting</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {exportOptions.map((option, i) => (
              <motion.div
                key={option.id}
                className="bg-obsidian-light border border-[#222] p-6 hover:border-crimson/30 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${option.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <option.icon className={`w-6 h-6 ${option.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-white mb-1">{option.title}</h3>
                    <p className="text-gray-400 text-sm mb-4">{option.description}</p>
                    
                    {/* Format Selection */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => option.action("json")}
                        disabled={!!exporting}
                        variant="outline"
                        className="border-[#333] text-white hover:border-blue-500 hover:bg-blue-500/10 flex-1"
                        data-testid={`export-${option.id}-json`}
                      >
                        {exporting === option.id ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <FileJson className="w-4 h-4 mr-2 text-blue-400" />
                        )}
                        JSON
                      </Button>
                      <Button
                        onClick={() => option.action("csv")}
                        disabled={!!exporting}
                        variant="outline"
                        className="border-[#333] text-white hover:border-green-500 hover:bg-green-500/10 flex-1"
                        data-testid={`export-${option.id}-csv`}
                      >
                        {exporting === option.id ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <Table className="w-4 h-4 mr-2 text-green-400" />
                        )}
                        CSV
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Info Section */}
          <motion.div
            className="mt-8 p-6 bg-obsidian-light border border-[#222]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-white mb-2">Export Information</h4>
                <ul className="text-gray-400 text-sm space-y-1">
                  <li>All exports are logged for audit purposes</li>
                  <li>JSON exports include all available fields</li>
                  <li>CSV exports are formatted for spreadsheet applications (Excel, Google Sheets)</li>
                  <li>Large exports may take a few seconds to generate</li>
                  <li>Sensitive data (passwords) is never included in exports</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default AdminExport;
